import { OUTREACH_LEAD_STATUSES, OUTREACH_VERTICALS, type OutreachLeadStatus } from '../../../shared/types';
import { formatDayMonth } from '../../components/primitives';
import { useIsMobile } from '../../lib/useIsMobile';
import { STATUS_LABELS, VERTICAL_LABELS } from './constants';
import { FilterChip, SectionHead } from './chrome';
import { LeadRow } from './LeadRow';
import type { OutreachListProps } from './types';

export function OutreachListBody({
  exportAction,
  ranked,
  totals,
  filters,
  onFilter,
  runDates,
  shortlistOpen,
  queuedOpen,
  onToggleSection,
  actions,
}: OutreachListProps) {
  const isMobile = useIsMobile();
  const shortlist = ranked.filter((e) => e.lead.verdict === 'shortlist');
  const queued = ranked.filter((e) => e.lead.verdict === 'queued');
  const countLabel = (shown: number, total: number) =>
    shown === total ? String(total) : `${shown} of ${total}`;
  const toggleStatus = (status: OutreachLeadStatus) =>
    onFilter({
      ...filters,
      statuses: filters.statuses.includes(status)
        ? filters.statuses.filter((s) => s !== status)
        : [...filters.statuses, status],
    });

  return (
    <div data-outreach-list>
      {/* One scrolling chip row on mobile (frame A) — same chips, same state,
          the mono "status / vertical / run" labels hidden by the block. */}
      <div className={isMobile ? 'chiprow outreach-mobile-filters' : 'outreach-filters'}>
        <span className="f-label">status</span>
        <FilterChip
          label="all"
          on={filters.statuses.length === 0}
          onClick={() => onFilter({ ...filters, statuses: [] })}
        />
        {OUTREACH_LEAD_STATUSES.map((status) => (
          <FilterChip
            key={status}
            label={STATUS_LABELS[status]}
            on={filters.statuses.includes(status)}
            onClick={() => toggleStatus(status)}
          />
        ))}
        <span className="outreach-fsep">|</span>
        <span className="f-label">vertical</span>
        <FilterChip
          label="all"
          on={filters.vertical === null}
          onClick={() => onFilter({ ...filters, vertical: null })}
        />
        {OUTREACH_VERTICALS.map((vertical) => (
          <FilterChip
            key={vertical}
            label={VERTICAL_LABELS[vertical]}
            on={filters.vertical === vertical}
            onClick={() => onFilter({ ...filters, vertical })}
          />
        ))}
        <span className="outreach-fsep">|</span>
        <span className="f-label">run</span>
        {runDates.map((runDate) => (
          <FilterChip
            key={runDate}
            label={formatDayMonth(runDate)}
            on={filters.runDate === runDate}
            onClick={() => onFilter({ ...filters, runDate }, true)}
          />
        ))}
        <FilterChip
          label="all"
          on={filters.runDate === null}
          onClick={() => onFilter({ ...filters, runDate: null }, true)}
        />
        {!isMobile && exportAction}
      </div>

      <div className="group">
        <SectionHead
          title="Shortlist"
          count={countLabel(shortlist.length, totals.shortlist)}
          note={isMobile ? 'ranked by score' : 'ranked by score · the working list'}
          open={shortlistOpen}
          onToggle={() => onToggleSection('shortlist')}
        />
        {shortlistOpen && (
          <>
            {shortlist.map((entry) => (
              <LeadRow key={entry.lead.id} entry={entry} actions={actions} quick />
            ))}
            {shortlist.length === 0 && (
              <div className="empty-note">Nothing shortlisted matches these filters.</div>
            )}
          </>
        )}
      </div>

      <div className="group">
        <SectionHead
          title="Queued"
          count={countLabel(queued.length, totals.queued)}
          note={
            isMobile ? 'score 40 to 69 · browsing only' : queuedOpen
              ? "score 40 to 69 · open a row to act on it"
              : "score 40 to 69 · browsing only, not this week's work"
          }
          open={queuedOpen}
          onToggle={() => onToggleSection('queued')}
        />
        {queuedOpen && (
          <>
            {queued.map((entry) => (
              <LeadRow key={entry.lead.id} entry={entry} actions={actions} quick={false} />
            ))}
            {queued.length === 0 && (
              <div className="empty-note">Nothing queued matches these filters.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
